/**
 * Pesos at the edge (T004).
 *
 * Every amount inside the books is an INTEGER number of centavos. This is the
 * only place a person's "85.50" becomes 8550 or 8550 becomes "85.50", and both
 * directions use string arithmetic — so "19.99" is exactly 1999, never
 * 1998.9999999999998, and nothing downstream ever holds a float it would have
 * to round.
 *
 * Kept inside the module rather than borrowed from inventory's copy: a module
 * does not import another module's code (Principle III).
 */

export class MoneyInputError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MoneyInputError";
  }
}

/**
 * "85.50" → 8550. Thousands may be grouped ("1,250.75"); a lone comma ("1,25")
 * is ambiguous and refused, as is a fraction of a centavo.
 */
export function parsePesos(text: string, options: { allowNegative?: boolean } = {}): number {
  const match = /^(-)?(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d{1,2}))?$/.exec(text.trim());
  if (!match) throw new MoneyInputError(`"${text}" is not an amount in pesos.`);

  const [, sign, whole = "0", fraction = ""] = match;
  if (sign && !options.allowNegative) throw new MoneyInputError(`"${text}" cannot be negative here.`);

  const value = Number(whole.replace(/,/g, "")) * 100 + Number(fraction.padEnd(2, "0"));
  if (!Number.isSafeInteger(value)) throw new MoneyInputError(`"${text}" is too large.`);
  return sign && value !== 0 ? -value : value;
}

/** 41657800 → "416,578.00". No currency symbol — that is the deployment's to choose. */
export function formatPesos(centavos: number): string {
  const magnitude = Math.abs(centavos);
  const text = `${Math.floor(magnitude / 100).toLocaleString("en-US")}.${String(magnitude % 100).padStart(2, "0")}`;
  return centavos < 0 ? `-${text}` : text;
}
